/**
 * SKOP Graduation Timer Component
 *
 * Displays predicted time until token graduates from bonding curve.
 */

import { useState, useEffect } from 'react';
import {
  predictGraduation,
  getConfidenceColor,
  getConfidenceLabel,
  type GraduationPrediction,
  type RecentTrade,
} from '../lib/graduation';

interface GraduationTimerProps {
  bondingCurveProgress: number;
  recentTrades: RecentTrade[];
  isGraduated?: boolean;
}

export function GraduationTimer({
  bondingCurveProgress,
  recentTrades,
  isGraduated = false,
}: GraduationTimerProps) {
  const [prediction, setPrediction] = useState<GraduationPrediction | null>(null);

  useEffect(() => {
    const calculate = () => {
      const pred = predictGraduation(bondingCurveProgress, recentTrades, isGraduated);
      setPrediction(pred);
    };

    calculate();

    // Recalculate every 30 seconds
    const interval = setInterval(calculate, 30000);
    return () => clearInterval(interval);
  }, [bondingCurveProgress, recentTrades, isGraduated]);

  if (!prediction) return null;

  // Don't show for graduated tokens
  if (prediction.isGraduated) {
    return (
      <div style={{
        padding: '12px 16px',
        background: 'rgba(0, 255, 136, 0.1)',
        border: '1px solid rgba(0, 255, 136, 0.3)',
        borderRadius: 'var(--radius-md)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <span style={{ width: '20px', height: '20px', display: 'inline-flex', color: 'var(--success)' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
            <path d="M6 12v5c3 3 9 3 12 0v-5"/>
          </svg>
        </span>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--success)' }}>
            Graduated!
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
            Trading on DEX
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '12px 16px',
      background: 'var(--bg-tertiary)',
      borderRadius: 'var(--radius-md)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ width: '16px', height: '16px', display: 'inline-flex', color: 'var(--accent)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </span>
          <span style={{ fontSize: '13px', fontWeight: 600 }}>Graduation Timer</span>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '11px',
          color: getConfidenceColor(prediction.confidence),
        }}>
          <span style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: getConfidenceColor(prediction.confidence),
          }} />
          {getConfidenceLabel(prediction.confidence)}
        </div>
      </div>

      {/* Main Prediction */}
      <div style={{
        textAlign: 'center',
        padding: '16px 0',
        borderBottom: '1px solid var(--border)',
        marginBottom: '12px',
      }}>
        <div style={{
          fontSize: '28px',
          fontWeight: 700,
          color: prediction.estimatedTimeMs ? 'var(--accent)' : 'var(--text-tertiary)',
          fontFamily: 'var(--font-mono)',
        }}>
          {prediction.estimatedTimeFormatted}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
          until graduation (at current pace)
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px',
      }}>
        <StatItem
          label="Progress"
          value={`${prediction.currentProgress.toFixed(1)}%`}
        />
        <StatItem
          label="Buy Rate"
          value={formatBuyRate(prediction.buyRatePerMinute)}
          subtext="/min"
        />
        <StatItem
          label="Recent Buys"
          value={prediction.recentBuys.toString()}
          subtext="last hour"
        />
      </div>

      {/* Progress Bar */}
      <div style={{ marginTop: '12px' }}>
        <div style={{
          height: '6px',
          background: 'var(--bg-secondary)',
          borderRadius: '3px',
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${Math.min(100, prediction.currentProgress)}%`,
            background: prediction.currentProgress > 90
              ? 'var(--success)'
              : prediction.currentProgress > 70
                ? 'var(--warning)'
                : 'var(--accent)',
            borderRadius: '3px',
            transition: 'width 0.3s ease',
          }} />
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '10px',
          color: 'var(--text-tertiary)',
          marginTop: '4px',
        }}>
          <span>0%</span>
          <span>Graduation at 100%</span>
        </div>
      </div>
    </div>
  );
}

function StatItem({
  label,
  value,
  subtext,
}: {
  label: string;
  value: string;
  subtext?: string;
}) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '2px' }}>
        {label}
      </div>
      <div style={{ fontSize: '14px', fontWeight: 600 }}>
        {value}
        {subtext && (
          <span style={{ fontSize: '10px', fontWeight: 400, color: 'var(--text-tertiary)' }}>
            {' '}{subtext}
          </span>
        )}
      </div>
    </div>
  );
}

function formatBuyRate(tokensPerMinute: number): string {
  if (tokensPerMinute === 0) return '0';
  if (tokensPerMinute >= 1_000_000) {
    return `${(tokensPerMinute / 1_000_000).toFixed(1)}M`;
  }
  if (tokensPerMinute >= 1_000) {
    return `${(tokensPerMinute / 1_000).toFixed(1)}K`;
  }
  return tokensPerMinute.toFixed(0);
}
